'use client';

import { useUserSession } from '@/components/contexts/user-context';
import { api } from '@/data/api';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, type ReactNode } from 'react';

const SESSION_KEY = 'bt.analytics.session';
const VISITOR_KEY = 'bt.analytics.visitor';

function randomId() {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().replaceAll('-', '');
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function stored(storage: Storage, key: string) {
    let value = storage.getItem(key);
    if (!value || value.length < 16) {
        value = randomId();
        storage.setItem(key, value);
    }
    return value;
}

function attribution() {
    const query = new URLSearchParams(window.location.search);
    return {
        source: query.get('utm_source') ?? undefined,
        medium: query.get('utm_medium') ?? undefined,
        campaign: query.get('utm_campaign') ?? undefined,
    };
}

export function AnalyticsProvider({ children }: { children: ReactNode }) {
    const pathname = usePathname();
    const user = useUserSession();
    const latestPath = useRef(pathname);
    const lastView = useRef('');
    const identifiers = useRef<{ sessionId: string; visitorId: string }>(null);

    useEffect(() => { latestPath.current = pathname; }, [pathname]);

    useEffect(() => {
        try {
            identifiers.current = {
                sessionId: stored(sessionStorage, SESSION_KEY),
                visitorId: stored(localStorage, VISITOR_KEY),
            };
        } catch {
            identifiers.current = { sessionId: randomId(), visitorId: randomId() };
        }
    }, []);

    async function track(pageView = false, force = false) {
        const ids = identifiers.current;
        if (!ids || (!force && document.visibilityState === 'hidden' && !pageView)) return;
        const body = {
            ...ids,
            path: latestPath.current || '/',
            pageView,
            title: document.title,
            referrer: document.referrer || undefined,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
            utm: attribution(),
            screen: { width: window.screen.width, height: window.screen.height, pixelRatio: window.devicePixelRatio },
        };
        await api.organization.analytics.session.fetch({ body }).catch(() => undefined);
    }

    useEffect(() => {
        if (!identifiers.current) return;
        const key = `${pathname}:${user?._id ?? 'anonymous'}`;
        if (lastView.current === key) return;
        lastView.current = key;
        void track(true);
    }, [pathname, user?._id]);

    useEffect(() => {
        const interval = window.setInterval(() => { if (document.visibilityState === 'visible') void track(false); }, 45_000);
        const visibility = () => { if (document.visibilityState === 'visible') void track(false); };
        const pagehide = () => void track(false, true);
        document.addEventListener('visibilitychange', visibility);
        window.addEventListener('pagehide', pagehide);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', visibility);
            window.removeEventListener('pagehide', pagehide);
        };
    }, []);

    return children;
}
